import React, { useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import { Button, Spin, message } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import { groupQueryNoticeDetail, confirmNotice, queryNoticeDetail } from '@/api/systermNotice';
import { DATE_TIME_FORMAT } from '@/config';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
// 引入wangEditor样式，确保表格等元素正确渲染
import '@wangeditor/editor/dist/css/style.css';
import styles from './index.module.scss';

export interface ISysViewDrawerRef {
  openDrawer: (params) => void;
}

const SysNoticeViewDrawer = React.forwardRef<ISysViewDrawerRef, any>((props, ref) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any>({});

  const { showConfirm } = props; // 是否展示 “确认收到”的按钮(只有门店层才需要展示 该按钮)

  const drawerRef = useRef<IdrawerRef>();
  const paramsRef = useRef<any>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    drawerRef.current.closeDrawer();
    setText('');
    setDetail({});
    if (props.callback) props.callback();
  };

  // 获取详情
  const initDetail = async (id) => {
    try {
      setLoading(true);
      const res = showConfirm ? await queryNoticeDetail(id) : await groupQueryNoticeDetail(id); // 集团和门店查看详情的接口不一致
      if (res?.code === 0) {
        setDetail(res.data || {});
        setText(res.data?.content);
      }
      console.log('res', res);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  // 确认收到
  const confirm = debounce(async () => {
    try {
      setLoading(true);
      const res = await confirmNotice({ groupId: paramsRef.current.groupId });
      if (res?.code === 0) {
        message.success('系统公告已确认收到');
        // 关闭弹框
        handleClose();
      }
      console.log('res', res);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  }, 300);

  const openDrawer = ({ id, groupId }) => {
    try {
      drawerRef.current.openDrawer();
      paramsRef.current = { id, groupId };
      // 获取公告详情
      initDetail(id);
    } catch (error) {
      console.log(error);
    }
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  // 使用useCallback优化事件处理函数
  const handleClick = useCallback((e: Event) => {
    e.preventDefault(); // 阻止默认行为
    e.stopPropagation(); // 阻止事件冒泡

    try {
      const target = e.target as HTMLElement;
      if (target instanceof HTMLAnchorElement) {
        const href = target.getAttribute('href');
        const fileName = target.textContent || '下载文件';

        console.log('点击了富文本中的链接:', href);

        // 这里可以添加你的自定义处理逻辑
        // 例如通过electron打开链接或下载文件
        if (window?.electron?.ipcRenderer?.download) {
          window.electron.ipcRenderer.download({
            downloadPath: href,
            fileName,
          });
        } else {
          // 如果没有electron环境
          window.open(href, '_blank');
        }
      }
    } catch (error) {
      console.error('处理链接点击出错:', error);
    }
  }, []);

  // 拦截富文本 a 标签点击事件，通过 jsBridge.openSystemBrowser 打开链接
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    container.addEventListener('click', handleClick);
    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  return (
    <>
      <CustomDrawer
        title="系统公告"
        ref={drawerRef}
        footer={
          <>
            {/* 确认收到按钮展示的逻辑： 1.门店测  2.公告的确认状态 = 未确认   3.确认失效未超过 */}
            {showConfirm && !detail?.confirmFlag && !detail?.overTimeFlag ? (
              <Button type="primary" loading={loading} onClick={() => confirm()}>
                确认收到
              </Button>
            ) : null}
            <Button type="ghost" onClick={() => handleClose()}>
              关闭
            </Button>
          </>
        }
        onClose={() => handleClose()}
      >
        <Spin spinning={loading}>
          <div className={styles.title}>{detail?.title || ''}</div>
          <div className={styles.publisher}>
            <div>
              {detail?.releaseBy || ''}
              {detail?.releaseName || ''}
            </div>
            <div className={styles.releaseTime}>
              {detail?.releaseTime ? moment(detail?.releaseTime).format(DATE_TIME_FORMAT) : ''}
            </div>
          </div>

          <div
            ref={contentRef}
            className={`${styles.content} richtext-content`}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        </Spin>
      </CustomDrawer>
    </>
  );
});

export default SysNoticeViewDrawer;
